import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api/rest";
import type { MakerArm } from "../api/types";

/**
 * Разрешение авторежима заявки на кромке по ОДНОМУ окну.
 *
 * # Почему опрос, а не только локальное состояние
 *
 * Потому что окно можно взвести из соседней вкладки, а тратит деньги при этом
 * трейдер. Переключатель, знающий только про свои нажатия, показывал бы
 * «выключено» над окном, по которому уходят ордера, — и это ровно то состояние,
 * в котором оператор перестаёт доверять экрану.
 *
 * # Почему опрос редкий
 *
 * Ключ меняется только рукой оператора, и таких нажатий единицы за смену. Пять
 * секунд — это компромисс с той самой соседней вкладкой: заметить чужое нажатие
 * надо, а держать вторую секундную петлю рядом со стаканом незачем.
 *
 * # Чего хук НЕ делает
 *
 * Не подставляет умолчаний. Пустая сумма остаётся пустой: авторежим без неё не
 * поставит ни одного ордера, и показать вместо неё «5» значило бы пообещать
 * размер, которого оператор не называл.
 */

/** Как часто перечитывать ключ окна. */
const POLL_MS = 5_000;

export function autoMakerKey(conditionID: string): readonly unknown[] {
  return ["automaker", conditionID];
}

export function useAutoMaker(conditionID: string | undefined) {
  return useQuery<MakerArm>({
    queryKey: autoMakerKey(conditionID ?? ""),
    queryFn: async () => (await api.automaker(conditionID!)).automaker,
    enabled: Boolean(conditionID),
    refetchInterval: POLL_MS,
    // placeholderData НЕ задаётся намеренно: прошлое окно, показанное, пока
    // грузится новое, рисовало бы взведённым рынок, который никто не взводил.
  });
}

/**
 * Взвод и снятие взвода.
 *
 * Ответ сервера кладётся в кэш СРАЗУ, не дожидаясь следующего опроса: между
 * нажатием и опросом до пяти секунд, и всё это время переключатель показывал бы
 * прежнее положение. Оптимистичного обновления при этом нет — положение
 * переключателя меняется только по ответу того, кто ключ записал.
 */
export function useSetAutoMaker(conditionID: string | undefined) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (req: { enabled: boolean; usd?: string }) =>
      (await api.setAutomaker(conditionID!, req)).automaker,
    onSuccess: (arm) => {
      client.setQueryData(autoMakerKey(conditionID ?? ""), arm);
    },
  });
}
